'use client';

import useGetImageUrlsByTags from '@/components/hooks/use-get-image-urls-by-tags';
import { usePathname } from 'next/navigation';
import React from 'react';
import { HERO_IMAGE_PROVIDER_INITIAL_STATE, MAX_RESULTS } from './consts';
import type { HeroImageProviderProperties } from './types';

export const HeroImageContext = React.createContext(
  HERO_IMAGE_PROVIDER_INITIAL_STATE,
);

export function useHeroImage() {
  return React.useContext(HeroImageContext);
}

// NOTE: HeroImageProvider uses the current path to get the hero
// image from Cloudinary by tags. Ex: hero_request_consultation
// This way, you can simply change the tag the change the hero image
function HeroImageProvider({ children }: HeroImageProviderProperties) {
  const pathname = usePathname();

  const tags = React.useMemo(
    () => `hero${pathname}`.replaceAll(/[\s/-]|(?:%20)/g, '_'),
    [pathname],
  );

  const { error, isLoading, data } = useGetImageUrlsByTags({
    maxResults: MAX_RESULTS,
    tags,
  });

  const context = React.useMemo(
    () => ({
      error: error?.message ?? null,
      isLoading,
      url: data && data.length > 0 ? data[0].public_id : null,
    }),
    [error, isLoading, data],
  );

  return (
    <HeroImageContext.Provider value={context}>
      {children}
    </HeroImageContext.Provider>
  );
}

export default React.memo(HeroImageProvider);
